import React, { useCallback } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Controller } from 'react-hook-form';
import { MdDiscount, MdEmail } from 'react-icons/md';
import { IOption } from '../../../../types/options';
import styles from '../styled-select.module.scss';
import {
  styleBase,
  styleBold,
  styleDiscount,
  styleRequest,
} from '../style';

interface SelectProps {
  name: string,
  control: any,
  options: IOption[],
  rules?: any,
  widthLabel?: string | null,
  inline?: boolean,
  placeholder?: string,
  isMulti?: boolean,
  isDisabled?: boolean,
  withColorDot?: boolean,
  label?: string | null,
  className?: string,
  discountEmail?: string | null,
  estimateId?: string | null,
  /** Defaults to `estimates` (mailto deep link). Use `etaiements` for étaiement documents. */
  discountDocumentSegment?: string,
  isClearable?: boolean,
  isSearchable?: boolean,
  focusOnError?: boolean,
  defaultValue?: any,
  defaultColor?: string | null,
  handleChange?: (val: any) => void,
  isCreatable?: boolean,
  formatCreateLabel?: (inputValue: string) => string,
  /** Render dropdown in document.body so menus are not clipped by overflow containers. */
  menuInPortal?: boolean,
  prop?: any,
}

const formatOptionLabel = ({
  label, value, withColorDot, getColor,
}
: { label: string, value: string, withColorDot: boolean, getColor: (value: string) => string }) => (
  <>
    {withColorDot && (
      <div
        style={getColor(value) ? { backgroundColor: getColor(value) } : {}}
        className={styles.dot}
      />
    )}
    <span>{label}</span>
  </>
);

const StyledSelect = ({
  name,
  control,
  rules,
  options = [],
  widthLabel = null,
  inline = false,
  placeholder = '',
  label = null,
  className,
  isClearable = false,
  isSearchable = true,
  isDisabled = false,
  withColorDot = false,
  focusOnError = true,
  defaultValue,
  defaultColor = null,
  isMulti = false,
  discountEmail = null,
  estimateId = null,
  discountDocumentSegment = 'estimates',
  handleChange,
  isCreatable,
  formatCreateLabel: formatCreateLabelProp,
  menuInPortal = false,
  ...props
}: SelectProps) => {
  function getClassName(color: string | null) {
    let style;
    if (className === 'bold') style = styleBold;
    if (className === 'discount') style = styleDiscount;
    if (className === 'request') style = styleRequest;
    else style = styleBase;
    return style(color);
  }

  const getColor = useCallback((value: string | number) => {
    const color = options.find((d: IOption) => d.value === value)?.color || defaultColor;
    return color;
  }, [options]);

  const getValue = (field: any) => {
    let value: any = null;

    if (isMulti && Array.isArray(field.value)) {
      value = options.filter((option: IOption) => field.value.includes(option.value));
    } else if (field.value || field.value === '') {
      value = options.find((option: IOption) => option.value === field.value);
      if (!value && isCreatable && field.value) {
        value = { label: field.value, value: field.value };
      }
    }
    return value;
  };

  const SelectComponent = isCreatable ? CreatableSelect : Select;

  const formatCreateLabel = formatCreateLabelProp
    ?? ((inputValue: string) => `Ajouter la phase "${inputValue}"`);

  const getSelectStyles = (fieldValue: string | number) => {
    const baseStyles = getClassName(!withColorDot ? getColor(fieldValue) : null);
    if (!menuInPortal) return baseStyles;
    return {
      ...baseStyles,
      menuPortal: (provided: Record<string, unknown>) => ({
        ...provided,
        zIndex: 9999,
      }),
    };
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({
        field,
      }) => (
        <div className={inline ? `${styles['container-select']} ${className === 'discount' ? styles?.discount : ''} ${styles.inline}` : `${styles['container-select']} ${className === 'discount' ? styles?.discount : ''}`}>
          {label && (
            <label
              style={widthLabel ? { minWidth: widthLabel } : {}}>
              {className === 'discount' && MdDiscount({})}
              {label}
              {rules && (<sup>*</sup>)}
              {className === 'discount' && discountEmail && <a className={styles.mail}
              href={`mailto:${discountEmail}?subject=Demande%20de%20remise&body=Consultez%20ce%20lien%3A%20${process.env.REACT_APP_FRONT_URL}/${discountDocumentSegment}/${estimateId || ''}`}>
                <br/>{MdEmail({})} Demander une remise supplémentaire</a>}
            </label>
          )}
          <SelectComponent
            {...field}
            {...props}
            isMulti={isMulti}
            ref={focusOnError ? field.ref : null}
            value={getValue(field)}
            // value={field?.value}
            options={options}
            onChange={(opt: any) => {
              field.onChange(isMulti ? opt.map((d: IOption) => d.value) : (opt?.value ?? null));
              if (handleChange) handleChange(opt);
            }}
            defaultValue={defaultValue}
            formatCreateLabel={formatCreateLabel}
            isDisabled={isDisabled}
            isClearable={isClearable}
            isSearchable={isSearchable}
            className={`${styles.select}`}
            placeholder={placeholder}
            formatOptionLabel={(p) => formatOptionLabel({ ...p, withColorDot, getColor })}
            styles={getSelectStyles(field.value)}
            menuPortalTarget={menuInPortal && typeof document !== 'undefined' ? document.body : undefined}
            menuPosition={menuInPortal ? 'fixed' : undefined}
          />
        </div>
      )}
    />
  );
};

export default StyledSelect;
